import { gql, NetworkStatus, useMutation, useQuery } from '@apollo/client'
import { useEffect } from 'react'

import { currentArtistVar, useGetNewestData } from '@/clients/utoniq-core'
import {
  ArtistBottomTabFragmentDoc,
  CurrentUserFragmentDoc,
  FetchNewNotificationsQuery,
  FetchNewNotificationsQueryVariables,
  NotificationBoxFragmentDoc,
  NotificationsConectionFragmentDoc,
  NotificationsQuery,
  NotificationsQueryVariables,
  UpdateLastNotificationReadAtMutation,
  UpdateLastNotificationReadAtMutationVariables,
} from '@/clients/utoniq-core/schema'
import { config } from '@/config'
import { makeViewModel } from '@/core/architecture'
import { setBadgeCount } from '@/core/native/badge'

import { Action, State } from './template'

export const useViewModel = makeViewModel<State, Action>(() => {
  const { isFirstLoadRef } = useGetNewestData<
    FetchNewNotificationsQuery,
    FetchNewNotificationsQueryVariables
  >({
    query: FETCH_NEW_QUERY,
    variables: {
      artistId: config.artistId,
      pageInput: { limit: 30 },
    },
    identify: {
      __typename: 'Organization',
      id: data => data?.organization?.id,
    },
    fields: [
      {
        fieldName: 'notifications',
        responsePath: 'organization.notifications',
        fragment: NotificationBoxFragmentDoc,
      },
    ],
    onCompleted(data) {
      if (data?.artist) {
        currentArtistVar(data.artist)
      }
    },
  })

  const { data, loading, fetchMore, networkStatus } = useQuery<
    NotificationsQuery,
    NotificationsQueryVariables
  >(QUERY, {
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: {
      artistId: config.artistId,
      isStandalone: config.applicationType === 'standalone',
    },
    onCompleted(data) {
      isFirstLoadRef.current = false
      if (data?.artist) {
        currentArtistVar(data.artist)
      }
    },
  })

  const [updateLastNotificationReadAt] = useMutation<
    UpdateLastNotificationReadAtMutation,
    UpdateLastNotificationReadAtMutationVariables
  >(UPDATE_LAST_NOTIFICATION_READ_AT)

  const isNextFetch = !!data

  useEffect(() => {
    // アクセス時に通知を既読にする
    updateLastNotificationReadAt({
      variables: {
        readAt: new Date(),
      },
    }).then(({ data }) => {
      setBadgeCount(data?.updateLastNotificationReadAt)
    })
  }, [])

  return {
    state: {
      loading: loading && !isNextFetch,
      refetching: networkStatus === NetworkStatus.fetchMore,
      hasNextPage: !!data?.organization?.notifications?.pageInfo.hasNextPage,
      user: data?.user ?? null,
      notifications: data?.organization?.notifications ?? null,
    },
    actions: {
      async onFetchMore() {
        await fetchMore({
          variables: {
            notificationsPageInput: {
              cursor:
                data?.organization?.notifications?.pageInfo.endCursor || null,
            },
          },
        })
      },
      async onFilter() {},
    },
  }
})

const QUERY = gql`
  query Notifications(
    $artistId: ID
    $notificationsPageInput: PageInputDate
    $isStandalone: Boolean!
  ) {
    artist(id: $artistId) @include(if: $isStandalone) {
      ...ArtistBottomTab
      currentLive {
        id
      }
    }
    organization {
      id
      notifications(pageInput: $notificationsPageInput) {
        ...NotificationsConection
      }
    }
    user {
      ...CurrentUser
    }
  }
  ${ArtistBottomTabFragmentDoc}
  ${NotificationsConectionFragmentDoc}
  ${CurrentUserFragmentDoc}
`

const FETCH_NEW_QUERY = gql`
  query FetchNewNotifications($artistId: ID, $pageInput: PageInputDate) {
    artist(id: $artistId) {
      ...ArtistBottomTab
      currentLive {
        id
      }
    }
    organization {
      id
      notifications(pageInput: $pageInput) {
        ...NotificationsConection
      }
    }
  }
  ${ArtistBottomTabFragmentDoc}
  ${NotificationsConectionFragmentDoc}
`

const UPDATE_LAST_NOTIFICATION_READ_AT = gql`
  mutation updateLastNotificationReadAt($readAt: Date!) {
    updateLastNotificationReadAt(readAt: $readAt)
  }
`
